import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Badge,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/contexts/ApiContext';
import { CordeCommonModelsDtoNotificationsNotificationChannel } from '@/api/generated/v1/api';
import { CreateChannelModal } from '@/components/notifications/CreateChannelModal';

export const NotificationChannelsTab: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // State
  const [channels, setChannels] = useState<CordeCommonModelsDtoNotificationsNotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<CordeCommonModelsDtoNotificationsNotificationChannel | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Theme colors
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load channels
  const loadChannels = async () => {
    try {
      setLoading(true);
      const response = await api.NotificationsApi.listChannels();
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Failed to load channels:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.loadChannelsError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  // Toggle channel enabled state
  const handleToggleChannel = async (channelId: string, isEnabled: boolean) => {
    try {
      await api.NotificationsApi.updateChannel(channelId, {
        channel: {
          id: channelId,
          is_enabled: isEnabled,
        },
        channel_id: channelId,
      });
      
      // Update local state
      setChannels(prev => 
        prev.map(channel => 
          channel.id === channelId 
            ? { ...channel, is_enabled: isEnabled }
            : channel
        )
      );
      
      toast({
        title: t('common.success'),
        description: t('notifications.channelUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to update channel:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.updateChannelError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Delete channel
  const handleDeleteChannel = async () => {
    if (!selectedChannel?.id) return;

    try {
      setDeleteLoading(true);
      await api.NotificationsApi.deleteChannel(selectedChannel.id);
      
      setChannels(prev => prev.filter(channel => channel.id !== selectedChannel.id));
      
      toast({
        title: t('common.success'),
        description: t('notifications.channelDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onDeleteClose();
    } catch (error) {
      console.error('Failed to delete channel:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.deleteChannelError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getChannelType = (channel: CordeCommonModelsDtoNotificationsNotificationChannel): string => {
    if (channel.email_config) return 'email';
    if (channel.telegram_config) return 'telegram';
    if (channel.whatsapp_config) return 'whatsapp';
    if (channel.sms_config) return 'sms';
    if (channel.push_config) return 'push';
    if (channel.slack_config) return 'slack';
    if (channel.discord_config) return 'discord';
    return 'unknown';
  };

  const getChannelTypeBadge = (type: string) => {
    const colorMap = {
      email: 'blue',
      telegram: 'cyan',
      whatsapp: 'green',
      sms: 'orange',
      push: 'purple',
      slack: 'purple',
      discord: 'indigo',
    };
    return (
      <Badge colorScheme={colorMap[type as keyof typeof colorMap] || 'gray'}>
        {type?.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Box>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="lg" fontWeight="semibold">
          {t('notifications.channelsManagement')}
        </Text>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={onCreateOpen}
        >
          {t('notifications.createChannel')}
        </Button>
      </Flex>

      {channels.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          {t('notifications.noChannels')}
        </Alert>
      ) : (
        <Box border="1px" borderColor={borderColor} borderRadius="md">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t('notifications.name')}</Th>
                <Th>{t('notifications.type')}</Th>
                <Th>{t('notifications.priority')}</Th>
                <Th>{t('notifications.enabled')}</Th>
                <Th>{t('notifications.created')}</Th>
                <Th>{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {channels.map((channel) => (
                <Tr key={channel.id}>
                  <Td>
                    <Box>
                      <Text fontWeight="semibold">{channel.name || t('notifications.unnamedChannel')}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {channel.description || t('notifications.noDescription')}
                      </Text>
                    </Box>
                  </Td>
                  <Td>
                    {getChannelTypeBadge(getChannelType(channel))}
                  </Td>
                  <Td>
                    <Badge variant="outline">{channel.priority || 0}</Badge>
                  </Td>
                  <Td>
                    <Switch
                      isChecked={channel.is_enabled}
                      onChange={(e) => handleToggleChannel(channel.id!, e.target.checked)}
                    />
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {channel.created_at ? new Date(channel.created_at).toLocaleDateString() : 'N/A'}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label={t('notifications.editChannel')}>
                        <IconButton
                          aria-label={t('notifications.editChannel')}
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            // TODO: Implement edit modal
                          }}
                        />
                      </Tooltip>
                      <Tooltip label={t('common.delete')}>
                        <IconButton
                          aria-label={t('common.delete')}
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            setSelectedChannel(channel);
                            onDeleteOpen();
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create Channel Modal */}
      <CreateChannelModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSuccess={loadChannels}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('notifications.deleteChannel')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('notifications.deleteChannelConfirm', { name: selectedChannel?.name })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteChannel}
                ml={3}
                isLoading={deleteLoading}
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}; 